import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getTag,
  createTag,
  updateTag,
  TAG_ID_REGEX,
} from "@/services/firebase/tag";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { AdminPage, AdminPageHeader, AdminLoadingSkeleton, AdminErrorAlert } from "@/components/admin-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const tagSchema = z.object({
  tag_id: z
    .string()
    .min(1, "Tag ID 為必填")
    .regex(TAG_ID_REGEX, "Tag ID 只能包含小寫字母、數字和底線"),
  zh: z.string().min(1, "簡體中文為必填"),
  tw: z.string().min(1, "繁體中文為必填"),
  en: z.string().min(1, "英文為必填"),
  ms: z.string().min(1, "馬來文為必填"),
});

type TagFormData = z.infer<typeof tagSchema>;

export default function TagEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState<string | null>(null);
  const isNew = id === "new" || id === undefined;
  const actualId = isNew ? undefined : id?.replace("/edit", "").replace("edit", "");

  // 載入標籤數據
  const { data: tag, isLoading } = useQuery({
    queryKey: ["tag", actualId],
    queryFn: () => getTag(actualId!),
    enabled: !isNew && !!actualId,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<TagFormData>({
    resolver: zodResolver(tagSchema),
    defaultValues: {
      tag_id: "",
      zh: "",
      tw: "",
      en: "",
      ms: "",
    },
  });

  // 初始化表單數據
  useEffect(() => {
    if (tag && !isNew) {
      setValue("tag_id", tag.tag_id || tag.id);
      setValue("zh", tag.zh || "");
      setValue("tw", tag.tw || "");
      setValue("en", tag.en || "");
      setValue("ms", tag.ms || "");
    }
  }, [tag, setValue, isNew]);

  const mutation = useMutation({
    mutationFn: async (data: TagFormData) => {
      if (isNew) {
        // 新增模式
        return await createTag(data);
      } else {
        // 編輯模式
        if (!actualId) {
          throw new Error("缺少標籤 ID");
        }
        // 編輯時不更新 tag_id（因為它是文檔 ID）
        await updateTag(actualId, {
          zh: data.zh,
          tw: data.tw,
          en: data.en,
          ms: data.ms,
        });
        return actualId;
      }
    },
    onSuccess: (savedId) => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["tag", savedId] });
      setSaveError(null);
      navigate("/tags");
    },
    onError: (error) => {
      console.error("儲存標籤失敗:", error);
      setSaveError(error instanceof Error ? error.message : "未知錯誤");
    },
  });

  const onSubmit = (data: TagFormData) => {
    // 確保 tag_id 為小寫
    const normalizedData = {
      ...data,
      tag_id: data.tag_id.toLowerCase(),
    };
    mutation.mutate(normalizedData);
  };

  if (!isNew && isLoading) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton variant="mixed" />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title={isNew ? "新增標籤" : "編輯標籤"}
        actions={
          <Button variant="outline" onClick={() => navigate("/tags")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回
          </Button>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* 錯誤訊息 */}
        {saveError && (
          <AdminErrorAlert
            message={`儲存失敗：${saveError}`}
            onDismiss={() => setSaveError(null)}
          />
        )}

        {/* 基本資訊 */}
        <Card>
          <CardHeader>
            <CardTitle>基本資訊</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tag_id">
                Tag ID <span className="text-destructive">*</span>
              </Label>
              <Input
                id="tag_id"
                {...register("tag_id")}
                placeholder="例如：romance"
                disabled={!isNew}
                style={{ textTransform: "lowercase" }}
                className={errors.tag_id ? "border-destructive" : ""}
              />
              {errors.tag_id && (
                <p className="text-sm text-destructive">{errors.tag_id.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                只能包含小寫字母、數字和底線，建立後無法修改
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 多語言設定 */}
        <Card>
          <CardHeader>
            <CardTitle>多語言設定</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tw">
                繁體中文 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="tw"
                {...register("tw")}
                placeholder="例如：愛情"
                className={errors.tw ? "border-destructive" : ""}
              />
              {errors.tw && (
                <p className="text-sm text-destructive">{errors.tw.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="zh">
                簡體中文 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="zh"
                {...register("zh")}
                placeholder="例如：爱情"
                className={errors.zh ? "border-destructive" : ""}
              />
              {errors.zh && (
                <p className="text-sm text-destructive">{errors.zh.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="en">
                英文 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="en"
                {...register("en")}
                placeholder="例如：Romance"
                className={errors.en ? "border-destructive" : ""}
              />
              {errors.en && (
                <p className="text-sm text-destructive">{errors.en.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="ms">
                馬來文 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ms"
                {...register("ms")}
                placeholder="例如：Romantik"
                className={errors.ms ? "border-destructive" : ""}
              />
              {errors.ms && (
                <p className="text-sm text-destructive">{errors.ms.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 操作按鈕 */}
        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/tags")}
          >
            取消
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {mutation.isPending ? "儲存中..." : "儲存"}
          </Button>
        </div>
      </form>
    </AdminPage>
  );
}
